// Zweck: Persistente Offline-Queue und Verarbeitung ausstehender Änderungen.
// Kontext: Datei-Uploads, HubSpot-Abgleich und Wiederholungen über IndexedDB.
// Stand: 2026-10-05
import { processOrderTask } from './orderTaskService';
import type { OrderTaskQueueData } from '../interfaces/orderTasks';
import { uploadDocumentImage, generateProjectDocumentPdf } from "./api/ops";
import { uploadSingleFileAsync, base64ToBytes, dataUrlToFile } from "./api/drive";
import { submitAbschlussbericht, upsertAbschlussberichtDraft } from "./api/abschlussbericht";
import { finalizeMaintenanceProtocol, uploadMaintenanceAttachment, upsertMaintenanceProtocol } from "./api/maintenance";
import { upsertProjektdokumentation, updateProjektdokumentationStatus as apiUpdateProjektdokumentationStatus } from "./api/projektdokumentation";
import { generateDocumentImageName, generateSerialNumberImageName, generateUniqueImageName, isJpegPhotoDataUrl, isPhotoDataUrl, normalizePhotoDataUrl } from "./imageService";
import localforage from "localforage";
import { useDocumentStore } from "../store/documentState";
import type { QueueItem, QueuedUploadFile, QueuedUploadFileRef, ProcessResult, DocumentUploadQueueData, DocumentUploadQueuePersistedData, HubspotTextPropertyQueueData, MaintenanceProtocolAttachmentQueueData, MaintenanceProtocolFinalizeQueueData, ProjectStatusRefreshDetail, ProjectStatusRefreshItem, QueueItemsPermanentlyFailedDetail, QueueItemsSyncFinishedDetail, ReworkCommentAddQueueData, VaillantSerialRegisterQueueData } from '../interfaces/queue';
import { useDealsStore } from "../store/dealsStore";
import { getSignedUrlAsync as getSignedUrl, updateTextPropertyAsync as updateDealTextProperty, getDealByIdAsync, addReworkCommentAsync } from "./api/hubspot";
import { registerVaillantSerialNumber } from "./api/vaillant";
import { createUploadDeadline } from "./uploadDeadline";
import { DOCUMENT_TO_PROPERTY_MAP, getDocumentTypeConfigById } from "./hubspotDealMapper";
import { AbschlussberichtDraft, AbschlussberichtSubmission } from "../types/abschlussbericht";
import type { MaintenanceProtocolUpsertPayload } from "../interfaces/maintenance";
import { resolvePhaseFolderName } from "../utils/phaseFolder";
import { UploadedDocumentFile } from "../types/documents";
import {
  normalizeUploadedDocumentFiles,
  mergeUploadedDocumentFiles,
  uploadedDocumentFilesToPropertyValue,
} from "../utils/uploadedDocumentFiles";
import {
  ProjektdokumentationFormData,
  ProjektdokumentationStatusUpdate,
} from "../types/projektdokumentation";
import { isQuotaExceededError } from "../utils/storageErrors";

// Upload-Queue-Service für Offline-Funktionalität

// IndexedDB Store konfigurieren
const queueStore = localforage.createInstance({
  name: "360volt-queue",
  storeName: "upload-queue",
});

// Separater Store (gleiche IndexedDB-Datenbank, eigener Object Store) nur für die großen
// Foto-/Datei-Payloads von document_upload/image_upload-Items (ADO #1216). QueueItem.data
// trägt für diese Typen nur noch eine Blob-ID (`imageDataRef`/`files[].blobRef`) statt der
// eingebetteten Base64-Daten - siehe extractQueueBlobs/hydrateQueueBlobs/removeQueueBlobs
// unten. Grund: Der Retry-Pfad (siehe processQueue) schreibt bei jedem Fehlversuch das
// komplette QueueItem neu, nur um den retries-Zähler zu erhöhen - läge das Foto weiterhin
// eingebettet in item.data, würde das bei jedem Retry mehrere MB unnötig neu in IndexedDB
// schreiben.
const queueBlobStore = localforage.createInstance({
  name: "360volt-queue",
  storeName: "upload-blobs",
});

// Queue-Item-Typen, deren `data` große Foto-/Datei-Payloads enthalten kann (imageData/
// files[].dataUrl) und daher über den Blob-Store laufen. "image_upload" hat aktuell keinen
// aktiven Enqueue-Aufrufer mehr, muss aber für ggf. noch in echten Nutzer-Queues liegende
// Alt-Items weiterhin unterstützt werden (Hydration/Cleanup).
const BLOB_QUEUE_TYPES = new Set(["document_upload", "image_upload"]);

const generateBlobId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `blob-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const writeQueueBlob = async (dataUrl: string): Promise<string> => {
  const id = generateBlobId();
  try {
    await queueBlobStore.setItem(id, dataUrl);
  } catch (error) {
    // Der Speicherplatzdruck ist mit diesem Store faktisch hierher gewandert (vorher schlug
    // ein volles Kontingent bei der eingebetteten Base64-Payload direkt in addQueueItem fehl,
    // das dafür bereits eine nutzerverständliche Meldung hat) - dieselbe Übersetzung gilt jetzt
    // auch hier, sonst sähe der Nutzer eine rohe IndexedDB-QuotaExceededError-Meldung
    // (Code-Review-Finding #1216).
    if (isQuotaExceededError(error)) {
      console.error("Speicherplatz erschöpft - Foto/Datei konnte nicht in die Offline-Queue geschrieben werden:", error);
      throw new Error("Speicherplatz auf dem Gerät ist voll. Bitte zuerst vorhandene Uploads synchronisieren.");
    }
    throw error;
  }
  return id;
};

/**
 * Wirft, wenn der Blob nicht (mehr) im Store liegt, statt einen leeren/undefined dataUrl
 * durchzureichen - ein daraus resultierender Upload-Versuch mit fehlendem Bild wäre ein
 * verwirrender Fehlschlag ohne erkennbare Ursache. Der Fehler hat keinen `.status`, wird von
 * processQueue also wie jeder andere transiente Fehler behandelt (Retry-Zähler erhöhen).
 *
 * Prüft explizit auf `null`/`undefined` statt auf Falsy (Code-Review-Finding #1216): ein
 * `!dataUrl`-Check würde einen (technisch validen, wenn auch unsinnigen) leeren String
 * ebenfalls als "fehlt" werten und wäre von einem echt fehlenden Blob nicht zu unterscheiden.
 */
const readQueueBlob = async (id: string): Promise<string> => {
  const dataUrl = await queueBlobStore.getItem<string>(id);
  if (dataUrl === null || dataUrl === undefined) {
    throw new Error(`Queue-Blob ${id} nicht gefunden`);
  }
  return dataUrl;
};

/**
 * Liest einen Blob NUR für eine UI-Vorschau eines noch wartenden Queue-Items (siehe
 * DocumentChecklist.getQueuedDocumentFiles/handleOpenLocalDocumentPreview) - anders als
 * readQueueBlob (das beim eigentlichen Upload-Versuch verwendet wird und dort bewusst wirft)
 * wirft diese Variante NICHT, wenn der Blob fehlt: für eine reine Vorschau ist das kein
 * Fehler, der irgendetwas blockieren darf, `undefined` bedeutet hier nur "keine Vorschau
 * verfügbar" (Code-Review-Finding #1216: ohne einen Lesepfad, der die neue Blob-Referenz-Form
 * versteht, verlieren offline wartende Uploads ihre Vorschau/lassen sich nicht mehr öffnen).
 */
export const peekQueueBlob = async (blobRef: string): Promise<string | undefined> => {
  const dataUrl = await queueBlobStore.getItem<string>(blobRef);
  return dataUrl ?? undefined;
};

/**
 * Beim Einreihen (addToQueue): große Foto-/Datei-Payloads einmalig in queueBlobStore
 * schreiben, `item.data` behält nur noch die Blob-Referenz. Für alle anderen Queue-Item-Typen
 * (nicht in BLOB_QUEUE_TYPES) unverändert durchgereicht.
 *
 * Schreibt die Dateien eines Multi-Uploads bewusst SEQUENZIELL (nicht mehr parallel) und räumt
 * bei einem Fehlschlag mitten in der Verarbeitung die in DIESEM Aufruf bereits erfolgreich
 * geschriebenen Blobs wieder auf, statt sie referenzlos verwaist im Blob-Store liegen zu
 * lassen (Code-Review-Finding #1216) - z.B. wenn von mehreren Fotos eines Multi-Uploads nur
 * eines am Speicherlimit scheitert. Der Aufrufer (QueueContext.addToQueue) übernimmt das
 * Aufräumen für den Fall, dass der nachfolgende addQueueItem-Aufruf fehlschlägt, obwohl die
 * Blobs hier bereits erfolgreich geschrieben wurden.
 */
export const extractQueueBlobs = async (type: string, data: unknown): Promise<unknown> => {
  if (!BLOB_QUEUE_TYPES.has(type) || typeof data !== "object" || data === null) {
    return data;
  }

  const result: Record<string, unknown> = { ...(data as Record<string, unknown>) };
  const writtenBlobIds: string[] = [];

  try {
    if (typeof result.imageData === "string") {
      const id = await writeQueueBlob(result.imageData);
      writtenBlobIds.push(id);
      result.imageDataRef = id;
      delete result.imageData;
    }

    if (Array.isArray(result.files)) {
      const refs: QueuedUploadFileRef[] = [];
      for (const file of result.files as unknown[]) {
        const candidate = file as Partial<QueuedUploadFile> | null | undefined;
        // Einträge ohne gültige dataUrl werden verworfen statt referenzlos durchgereicht -
        // konsistent mit getDocumentUploadFiles, das solche Einträge beim eigentlichen Upload
        // ohnehin ignoriert (Code-Review-Finding #1216: sonst hätte `files: QueuedUploadFileRef[]`
        // Einträge ohne `blobRef`, obwohl der Typ ihn als Pflichtfeld deklariert).
        if (typeof candidate?.dataUrl !== "string") continue;
        const { dataUrl, ...rest } = candidate;
        const blobRef = await writeQueueBlob(dataUrl);
        writtenBlobIds.push(blobRef);
        refs.push({ ...rest, blobRef });
      }
      result.files = refs;
    }
  } catch (error) {
    await Promise.allSettled(writtenBlobIds.map((id) => queueBlobStore.removeItem(id)));
    throw error;
  }

  return result;
};

/**
 * Gegenstück zu extractQueueBlobs: liest Blob-Referenzen aus queueBlobStore und baut daraus
 * wieder ein Objekt mit `imageData`/`files[].dataUrl`, wie es processImageUpload/
 * processDocumentUpload erwarten - diese Funktionen bleiben dadurch unverändert.
 *
 * Rückwärtskompatibilität (#1216-Akzeptanzkriterium "bereits wartende Queue-Items werden
 * migriert oder bleiben lesbar"): Items, die vor diesem Deploy eingereiht wurden, tragen noch
 * das alte Inline-Format (imageData/dataUrl direkt, keine *Ref-Felder) - die werden hier
 * unverändert durchgereicht, kein separater Migrations-Schritt beim App-Start nötig.
 *
 * Mutiert `data` NICHT und gibt eine neue Objektreferenz zurück - der Aufrufer (processQueue)
 * behält daneben weiterhin das ursprüngliche `item` mit der Blob-Referenz-Form. Das ist
 * bewusst so: der Retry-Pfad in processQueue schreibt genau dieses Original-Item zurück -
 * würde hydrateQueueBlobs stattdessen `data` in-place mutieren, würde ein nachfolgender Retry
 * versehentlich wieder die vollen Base64-Daten in IndexedDB schreiben und den ganzen Zweck
 * dieses Blob-Stores aushebeln.
 */
const hydrateQueueBlobs = async (type: string, data: unknown): Promise<unknown> => {
  if (!BLOB_QUEUE_TYPES.has(type) || typeof data !== "object" || data === null) {
    return data;
  }

  // `result` deckt bewusst beide Formen ab (persistiert mit *Ref-Feldern ODER hydriert mit
  // dataUrl-Feldern, je nach dem ob es sich um ein neues oder ein Altformat-Item handelt) -
  // dafür als Record<string, unknown> statt der strikten DocumentUploadQueuePersistedData
  // typisiert, sonst lässt sich `files` unten nicht von der Ref- in die dataUrl-Form
  // umschreiben.
  const persisted = data as DocumentUploadQueuePersistedData;
  const result: Record<string, unknown> = { ...(data as Record<string, unknown>) };

  if (typeof persisted.imageDataRef === "string") {
    result.imageData = await readQueueBlob(persisted.imageDataRef);
    delete result.imageDataRef;
  }

  if (Array.isArray(persisted.files)) {
    result.files = await Promise.all(
      persisted.files.map(async (file): Promise<QueuedUploadFile | QueuedUploadFileRef> => {
        if (typeof file?.blobRef !== "string") return file as unknown as QueuedUploadFile; // Altformat: dataUrl bereits inline
        const { blobRef, ...rest } = file;
        const dataUrl = await readQueueBlob(blobRef);
        return { ...rest, dataUrl } satisfies QueuedUploadFile;
      })
    );
  }

  return result;
};

/**
 * Löscht die von einem Queue-Item referenzierten Blobs, nachdem das Item selbst final aus
 * IndexedDB entfernt wurde (Erfolg oder permanenter 4xx-Fehler) bzw. der Nutzer es manuell
 * entfernt hat. Bewusst NICHT bei erschöpften Retries (retries >= 5) aufgerufen - solche Items
 * bleiben in der Queue liegen (siehe processQueue), ihr Blob wird weiterhin gebraucht.
 * Einzelne Fehler werden nur geloggt (Promise.allSettled) - ein verwaister Blob ist ein Leck,
 * darf aber den eigentlichen Sync-Erfolg nicht gefährden.
 */
export const removeQueueBlobs = async (type: string, data: unknown): Promise<void> => {
  if (!BLOB_QUEUE_TYPES.has(type) || typeof data !== "object" || data === null) {
    return;
  }

  const persisted = data as DocumentUploadQueuePersistedData;
  const ids: string[] = [];
  if (typeof persisted.imageDataRef === "string") ids.push(persisted.imageDataRef);
  if (Array.isArray(persisted.files)) {
    for (const file of persisted.files) {
      if (typeof file?.blobRef === "string") ids.push(file.blobRef);
    }
  }

  if (ids.length === 0) return;

  const results = await Promise.allSettled(ids.map((id) => queueBlobStore.removeItem(id)));
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.error("[queueService] Queue-Blob konnte nicht gelöscht werden:", result.reason);
    }
  });
};

const toProjectStatusRefreshItem = (item: QueueItem, resultData: unknown): ProjectStatusRefreshItem => {
  const payload = typeof item.data === 'object' && item.data !== null
    ? item.data as Record<string, unknown>
    : undefined;
  // Nur übernehmen, wenn resultData ein Objekt mit einer string-ID ist (z.B. der von
  // processReworkCommentAdd zurückgegebene ReworkComment) — andere Queue-Item-Typen liefern
  // hier `undefined`, resultId bleibt dann unset (siehe ProjectStatusRefreshItem-Doku).
  const resultId =
    typeof resultData === 'object' && resultData !== null && typeof (resultData as Record<string, unknown>).id === 'string'
      ? ((resultData as Record<string, unknown>).id as string)
      : undefined;

  return {
    id: item.id,
    type: item.type,
    projectId: typeof payload?.projectId === 'string' ? payload.projectId : undefined,
    protocolId: typeof payload?.protocolId === 'string' ? payload.protocolId : undefined,
    resultId,
  };
};

const notifyProjectStatusRefresh = (successfulItems: QueueItem[], resultDataById: Map<string, unknown>) => {
  if (typeof window === "undefined" || successfulItems.length === 0) {
    return;
  }

  const detail: ProjectStatusRefreshDetail = {
    successfulIds: successfulItems.map((item) => item.id),
    successfulItems: successfulItems.map((item) => toProjectStatusRefreshItem(item, resultDataById.get(item.id))),
  };

  window.dispatchEvent(
    new CustomEvent<ProjectStatusRefreshDetail>("project-status-refresh", {
      detail,
    })
  );
};

/**
 * Globales Event für Queue-Items, die `processQueue` gerade endgültig verworfen hat (4xx oder
 * Retry-Erschöpfung, siehe `permanentlyFailed`-Dokumentation weiter unten). Analog zu
 * `notifyProjectStatusRefresh`: ein Aufrufer wie AbschlussberichtWizard, der ein Item per
 * `addToQueue` offline eingereiht hat, bekommt dessen tatsächliches Sync-Ergebnis NICHT über
 * die eigene addToQueue-Promise mit (die löst beim Offline-Enqueue sofort auf, bevor überhaupt
 * synchronisiert wurde) - nur ein globales Event erreicht ihn auch dann noch, wenn der
 * eigentliche Sync-Versuch viel später (z.B. beim nächsten Online-Reconnect) in einem
 * komplett anderen Aufrufkontext (QueueContext.handleOnlineStatus) stattfindet
 * (PR-Review-Finding zu #976: ohne dieses Event blieb ein optimistisch gesetzter lokaler
 * State nach einem späteren permanenten Fehlschlag für immer als "abgeschickt" stehen).
 */
const notifyQueueItemsPermanentlyFailed = (permanentlyFailedItems: QueueItem[]) => {
  if (typeof window === "undefined" || permanentlyFailedItems.length === 0) {
    return;
  }

  const detail: QueueItemsPermanentlyFailedDetail = { permanentlyFailedItems };

  window.dispatchEvent(
    new CustomEvent<QueueItemsPermanentlyFailedDetail>("queue-item-permanently-failed", {
      detail,
    })
  );
};

/**
 * Globales Event, das für JEDES Item feuert, dessen In-Flight-Status in `processQueue` endet -
 * unabhängig vom Ausgang. Anders als `notifyProjectStatusRefresh` (nur Erfolg) und
 * `notifyQueueItemsPermanentlyFailed` (nur endgültiger 4xx-/Erschöpfungs-Fehlschlag) deckt
 * dieses Event auch den dritten, transienten Fall ab (Netzwerkfehler/Timeout/5xx): das Item
 * bleibt mit erhöhtem Retry-Zähler in der Queue, `inFlightItemIds.delete` läuft aber
 * genauso. Ein Aufrufer wie AbschlussberichtWizard, der wegen `isQueueItemInFlight` einen
 * neueren Payload zurückgehalten hat, braucht genau dieses eine verlässliche Signal "das
 * blockierende Item ist jetzt fertig verarbeitet, `isQueueItemInFlight` liefert wieder
 * `false`" (PR-Review-Finding, Re-Review zu #976: die beiden anderen Events allein ließen den
 * zurückgehaltenen Payload nach einem transienten Fehlschlag ohne weitere Nutzeränderung
 * unbegrenzt liegen).
 */
const notifyQueueItemsSyncFinished = (finishedIds: string[]) => {
  if (typeof window === "undefined" || finishedIds.length === 0) {
    return;
  }

  const detail: QueueItemsSyncFinishedDetail = { finishedIds };

  window.dispatchEvent(
    new CustomEvent<QueueItemsSyncFinishedDetail>("queue-item-sync-finished", {
      detail,
    })
  );
};

/**
 * Timeout-Helper: bricht den übergebenen AbortController bei Ablauf ab, damit der
 * zugrunde liegende Request wirklich abgebrochen wird (statt nur weiterzulaufen,
 * während das Item schon für einen Retry neu eingereiht wird → doppelte Uploads).
 */
const timeoutPromise = (
  ms: number,
  controller: AbortController,
): { promise: Promise<never>; cancel: () => void } => {
  let cancel = () => {};
  const promise = new Promise<never>((_, reject) => {
    // Pausierbar (siehe uploadDeadline.ts): Wartezeit auf die Drive-Ordner-Anlage zählt nicht
    // zum Upload-Budget, sonst brechen wartende Items trotz schnellem eigenen Request ab.
    cancel = createUploadDeadline(controller.signal, ms, () => {
      controller.abort();
      reject(new Error(`Timeout after ${ms}ms`));
    }).cancel;
  });
  // Ohne Handler würde ein abgebrochener (nicht mehr awaiteter) Timer-Promise als unhandled gelten.
  promise.catch(() => {});
  return { promise, cancel };
};

/**
 * processQueue verarbeitet alle validen Items parallel (Promise.allSettled) — ohne
 * Sperre könnten zwei Multi-Upload-Retry-Items für dieselbe dealId/property gleichzeitig
 * den Live-Wert lesen, jeweils ihre eigene Datei hineinmergen und anschließend gewinnt
 * der zeitlich letzte PATCH, wodurch die andere Datei aus dem Property verschwindet.
 * Dieser Lock serialisiert Read-Merge-Write pro dealId+property, sodass ein zweiter
 * Retry erst nach dem PATCH des ersten erneut liest.
 */
const hubspotPropertyLocks = new Map<string, Promise<unknown>>();

function withHubspotPropertyLock<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const previous = hubspotPropertyLocks.get(key) ?? Promise.resolve();
  const result = previous.then(fn, fn);
  hubspotPropertyLocks.set(key, result.catch(() => undefined));
  return result;
}

/**
 * Serialisiert alle Schreib-/Lösch-Zugriffe auf DASSELBE Queue-Item (per ID) - analog zu
 * `withHubspotPropertyLock`, aber generisch pro Queue-Item statt pro dealId+property.
 * PR-Review-Finding (3. Re-Review zu #976): ein "erst lesen, dann vergleichen, dann löschen"
 * (siehe `removeStaleQueueItem` unten) ist ohne diesen Lock kein atomarer Vorgang - zwischen
 * dem Lesen und dem Löschen kann ein `addQueueItem`-Aufruf für dieselbe ID (z.B. ein weiterer,
 * noch nicht gesendeter Autosave-Versuch) dazwischenlaufen und würde von der anschließenden
 * Löschung mitgerissen, obwohl er neuer als das ursprünglich gelesene Item ist. `addQueueItem`
 * und `removeStaleQueueItem` teilen sich denselben Lock pro ID; `removeQueueItem` bleibt
 * bewusst außerhalb (wird von `removeStaleQueueItem` selbst INNERHALB des gehaltenen Locks
 * aufgerufen - ein erneutes Sperren hier würde sich selbst blockieren).
 *
 * Der interne Lock-Chain-Eintrag (`queueItemLocks`) hängt sich IMMER an das ECHTE Ergebnis von
 * `fn()`, nie an ein zeitlich verkürztes Ersatzsignal (PR-Review-Finding, 6. Re-Review zu #976
 * - vorherige Fassungen gaben den Lock für NACHFOLGENDE Operationen nach 10s frei, obwohl `fn()`
 * selbst noch lief; eine später doch noch abschließende alte IndexedDB-Operation - z.B. ein
 * hängendes `removeItem` - konnte dadurch NACH einer inzwischen bereits gestarteten und
 * abgeschlossenen neueren Operation für dieselbe ID physisch ausgeführt werden und deren Stand
 * überschreiben/löschen. Eine zusätzliche Compare-and-Swap-Prüfung direkt vor der Mutation
 * schloss dieses Fenster nicht vollständig, weil Vergleich und Mutation weiterhin zwei
 * getrennte IndexedDB-Operationen blieben. Mit dieser Fassung bleiben alle physischen
 * Schreib-/Löschvorgänge für dieselbe ID strikt seriell - eine neue Operation beginnt mit
 * IHREM `fn()` erst, wenn die vorherige tatsächlich abgeschlossen ist, egal wie lange das
 * dauert.
 *
 * Damit eine einzelne wirklich hängende IndexedDB-Operation (bekannte Safari-/Private-Mode-/
 * Storage-Pressure-Eigenart) trotzdem nicht die aufrufende UI (z.B. den Draft-Autosave) für den
 * Rest der Session blockiert, bekommt NUR der jeweilige AUFRUFER ein durch ein 10s-Timeout
 * begrenztes Ergebnis (Reject) - unabhängig davon, ob `fn()` selbst inzwischen abgeschlossen
 * ist oder weiter im Hintergrund läuft. Das betrifft auch nachfolgende Aufrufe für dieselbe ID:
 * sie warten zwar weiterhin auf den tatsächlichen Abschluss der vorherigen Operation, bevor ihr
 * eigenes `fn()` startet, erhalten aber selbst spätestens nach 10s ein Ergebnis und hängen die
 * aufrufende Stelle damit nie unbegrenzt auf. Bleibt eine Operation tatsächlich für immer
 * hängen, bleiben künftige Schreib-/Löschversuche für GENAU DIESE ID zwar dauerhaft
 * unausgeführt (bewusst in Kauf genommen) - das ist der sichere Kompromiss gegenüber einem für
 * den Nutzer unsichtbaren Datenverlustfenster.
 *
 * Der Timeout-Reject ist dabei ausdrücklich ein UNBESTIMMTER Ausgang, kein definitiver
 * Fehlschlag von `fn()` (siehe `QueueItemLockTimeoutError`): Aufrufer mit destruktiver
 * Fehler-Kompensation (z.B. Rollback bereits geschriebener Nebeneffekte) müssen diesen
 * Fehlertyp gesondert behandeln und dürfen dafür nicht kompensieren, weil `fn()` das noch
 * erfolgreich nachholen kann.
 */
const queueItemLocks = new Map<string, Promise<unknown>>();
const QUEUE_ITEM_LOCK_TIMEOUT_MS = 10000;

/**
 * Markiert einen `withQueueItemLock`-Reject als UNBESTIMMTEN Ausgang (Timeout), nicht als
 * definitiven Fehlschlag von `fn()` selbst (PR-Review-Finding zu PR #422/#976: ein Aufrufer,
 * der auf jeden Reject hin bereits geschriebene Nebeneffekte kompensierend zurückrollt - z.B.
 * QueueContext.addToQueue, das bei einem addQueueItem-Fehlschlag die zuvor extrahierten Blobs
 * per removeQueueBlobs löscht -, darf das für DIESEN Fehlertyp NICHT tun: `fn()` läuft im
 * Hintergrund weiter und kann noch erfolgreich abschließen, siehe Doku unten. Eine Kompensation
 * würde dann Referenzen (z.B. `blobRef`/`imageDataRef`) löschen, die das verspätet doch noch
 * geschriebene Queue-Item weiterhin referenziert - dauerhaft beschädigter Upload statt eines
 * bloß verwaisten Blobs. Aufrufer mit destruktiver Kompensation müssen `instanceof` prüfen und
 * für diesen Fall darauf verzichten.
 */
export class QueueItemLockTimeoutError extends Error {
  /**
   * Löst auf, sobald `fn()` TATSÄCHLICH abgeschlossen ist (egal wie lange das nach diesem
   * Timeout noch dauert) - erfüllt bei Erfolg, verworfen mit dem echten Fehler von `fn()` bei
   * einem tatsächlichen Fehlschlag. PR-Review-Finding (9. Re-Review zu #976/#422): der reine
   * Reject dieser Klasse sagt Aufrufern zwar "unbestimmt, kein definitiver Fehlschlag", liefert
   * aber selbst keinen Zeitpunkt, ab dem der tatsächliche Ausgang feststeht - ohne dieses Feld
   * konnte z.B. QueueContext.addToQueue den späten Abschluss nicht bis in `items`/einen
   * tatsächlichen Sync-Versuch nachführen, und ein Aufrufer wie ReworkCommentThread sah seinen
   * optimistischen Kommentar nie zuverlässig bestätigt.
   */
  readonly settled: Promise<void>;

  constructor(id: string, settled: Promise<void>) {
    super(`Queue-Item-Operation für ${id} hat das Zeitlimit von ${QUEUE_ITEM_LOCK_TIMEOUT_MS}ms überschritten.`);
    this.name = 'QueueItemLockTimeoutError';
    this.settled = settled;
  }
}

function withQueueItemLock<T>(id: string, fn: () => Promise<T>): Promise<T> {
  const previous = queueItemLocks.get(id) ?? Promise.resolve();
  const result = previous.then(fn, fn);
  // Der nächste Aufruf für dieselbe ID hängt sich an DIESES echte Ergebnis, nicht an ein
  // zeitlich verkürztes - siehe Doku oben, das ist der Kern der Serialisierungsgarantie.
  queueItemLocks.set(id, result.catch(() => undefined));

  return new Promise<T>((resolve, reject) => {
    const timeoutHandle = setTimeout(() => {
      console.warn(
        `Queue-Item-Operation für ${id} läuft seit über ${QUEUE_ITEM_LOCK_TIMEOUT_MS}ms ohne Ergebnis (hängende IndexedDB-Operation?) - Aufrufer erhält einen Fehler, die Operation selbst läuft im Hintergrund weiter und blockiert nachfolgende Operationen für dieselbe ID bis zu ihrem tatsächlichen Abschluss.`
      );
      reject(new QueueItemLockTimeoutError(id, result.then(() => undefined)));
    }, QUEUE_ITEM_LOCK_TIMEOUT_MS);
    result.then(
      (value) => {
        clearTimeout(timeoutHandle);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutHandle);
        reject(error);
      }
    );
  });
}

/**
 * Queue aus IndexedDB laden
 */
export const loadQueue = async (): Promise<QueueItem[]> => {
  try {
    // Alle Queue-Items aus IndexedDB parallel laden statt sequenziell.
    const keys = await queueStore.keys();
    const items = await Promise.all(keys.map((key) => queueStore.getItem<QueueItem>(key)));

    return items.filter((item): item is QueueItem => item != null);
  } catch (error) {
    console.error("Fehler beim Laden der Queue aus IndexedDB:", error);
    return [];
  }
};

/**
 * Queue-Item zur IndexedDB hinzufügen
 *
 * Wirft bei einem Persistenzfehler (z.B. Quota erschöpft durch viele
 * gesammelte Foto-Base64-Payloads), statt ihn nur zu loggen. Ein Aufrufer
 * wie AbschlussberichtWizard verlässt sich darauf, dass ein Fehlschlag
 * hier tatsächlich sichtbar wird (Reject bis zu addToQueue durchgereicht) -
 * vorher ging das Item hier soundlos verloren, ohne dass Nutzer oder UI
 * je davon erfuhren (Ursache für #1199: "Daten fehlen", Abschluss zeigte
 * trotzdem "gespeichert" an).
 *
 * Läuft über `withQueueItemLock` (PR-Review-Finding, 3. Re-Review zu #976): ohne diesen Lock
 * könnte dieser Schreibvorgang mitten in ein `removeStaleQueueItem` für dieselbe ID (Lesen +
 * Vergleichen + Löschen, siehe dort) hineinlaufen und von dessen Löschung mitgerissen werden.
 *
 * `resolveItem` (optional) erhält den AKTUELL in IndexedDB persistierten Stand für diese ID
 * (`null`, falls noch keiner existiert) und liefert das tatsächlich zu schreibende Item zurück -
 * das Lesen läuft dabei innerhalb DESSELBEN `withQueueItemLock`-Aufrufs wie das anschließende
 * Schreiben (PR-Review-Finding, 10. Re-Review zu #976/#422, MaintenanceProtocols.uploadFiles):
 * ein vorheriger `addQueueItem`-Aufruf für dieselbe ID, der wegen eines Lock-Timeouts bereits mit
 * `QueueItemLockTimeoutError` abgelehnt hat, aber im Hintergrund weiterläuft (siehe
 * `withQueueItemLock`-Doku), ist dank der Serialisierung über denselben Lock garantiert
 * abgeschlossen, BEVOR dieses Lesen stattfindet. Ein Aufrufer, der stattdessen VOR diesem Aufruf
 * per `loadQueue()` liest und das Ergebnis erst danach hier hineinreicht, sieht einen noch
 * laufenden Schreibvorgang dagegen nicht - ein knapp danach eingereihter zweiter Schreibvorgang
 * für dieselbe ID würde ihn beim Auflösen dann trotzdem vollständig ersetzen, statt mit ihm
 * zusammengeführt zu werden.
 *
 * Gibt das TATSÄCHLICH gespeicherte Item zurück (PR-Review-Finding, 11. Re-Review zu
 * #976/#422, MaintenanceProtocols.uploadFiles-Online-Pfad): bei einem `resolveItem`-Merge weicht
 * das gespeicherte Item vom übergebenen `item` ab (z.B. A+B statt nur B). Ein Aufrufer, der
 * direkt danach die sofortige Verarbeitung anstößt, muss diesen gemergten Stand verwenden -
 * verarbeitet er stattdessen weiterhin das ursprüngliche `item`, würde der erfolgreiche Abschluss
 * (`processQueue` entfernt die ID) den gerade erst zusammengeführten Stand aus IndexedDB löschen,
 * ohne dass der nicht mitverarbeitete Teil (A) je hochgeladen wurde.
 */
export const addQueueItem = async (
  item: QueueItem,
  resolveItem?: (current: QueueItem | null) => QueueItem
): Promise<QueueItem> => {
  return withQueueItemLock(item.id, async () => {
    let itemToStore = item;
    if (resolveItem) {
      let current: QueueItem | null;
      try {
        current = await queueStore.getItem<QueueItem>(item.id);
      } catch (error) {
        console.error("Queue-Item konnte für das Zusammenführen nicht gelesen werden:", item.id, error);
        throw error;
      }
      itemToStore = resolveItem(current);
    }
    try {
      await queueStore.setItem(itemToStore.id, itemToStore);
    } catch (error) {
      if (isQuotaExceededError(error)) {
        console.error("Speicherplatz erschöpft - Queue-Item konnte nicht gespeichert werden:", itemToStore.id, error);
        throw new Error("Speicherplatz auf dem Gerät ist voll. Bitte zuerst vorhandene Uploads synchronisieren.");
      }
      console.error("Fehler beim Speichern des Queue-Items in IndexedDB:", error);
      throw error;
    }
    return itemToStore;
  });
};

/**
 * Queue-Item aus IndexedDB entfernen
 *
 * Wirft bei einem Fehler, statt ihn nur zu loggen (Code-Review-Finding zu #1199, analog zu
 * addQueueItem oben). Ohne das würde z.B. QueueContext.removeFromQueue trotz fehlgeschlagener
 * IndexedDB-Löschung optimistisch `setItems` aufrufen — das Item verschwindet dann aus der
 * UI, obwohl es weiterhin durabel in der Queue liegt und nach einem Reload (oder dem
 * nächsten loadQueue()) unerwartet wieder auftaucht. Für ein bestätigungspflichtiges Item
 * wie vaillant_serial_register wäre das besonders gefährlich: ein längst durch eine
 * erfolgreiche Live-Registrierung überholtes Item könnte erneut bestätigt werden und Vaillant
 * ein weiteres Mal aufrufen. processQueue (zwei interne Aufrufer) behandelt ein hier
 * geworfenes Reject bereits korrekt als "kein fachlicher 4xx, konservativ nicht als permanent
 * werten" (siehe dortiger Kommentar).
 */
export const removeQueueItem = async (id: string): Promise<void> => {
  try {
    await queueStore.removeItem(id);
  } catch (error) {
    console.error(
      "Fehler beim Entfernen des Queue-Items aus IndexedDB:",
      error
    );
    throw error;
  }
};

/**
 * Entfernt ein Queue-Item nur, wenn `isStale` für dessen AKTUELLEN Inhalt zutrifft - Lesen,
 * Vergleichen und Entfernen laufen dabei innerhalb desselben `withQueueItemLock` für diese ID,
 * damit ein dazwischenlaufender `addQueueItem`-Aufruf für dieselbe ID (z.B. ein weiterer, noch
 * nicht gesendeter Autosave-Versuch mit neuerem Payload) nicht zwischen Vergleich und Löschung
 * einstreuen und mitgelöscht werden kann (PR-Review-Finding, 3. Re-Review zu #976: ein reiner
 * `loadQueue()`+`removeQueueItem(id)`-Zweisatz beim Aufrufer war kein atomarer Vorgang - das
 * TOCTOU-Fenster dazwischen konnte trotz vorherigem `updatedAt`-Vergleich einen währenddessen
 * eingereihten neueren Payload verlieren).
 *
 * Schlägt das eigentliche Entfernen fehl (z.B. IndexedDB-Fehler), wird das Item stattdessen mit
 * `replacementItem` überschrieben statt mit seinem veralteten Inhalt liegen zu bleiben - reines
 * Loggen würde bedeuten, dass ein späterer automatischer Sync-Versuch dieses Items den gerade
 * anderswo erfolgreich gespeicherten (neueren) Stand wieder überschreiben könnte. Gibt zurück,
 * ob tatsächlich ENTFERNT wurde (nicht nur überschrieben) - Aufrufer können das nutzen, um z.B.
 * einen React-Queue-State zu synchronisieren.
 *
 * Scheitert auch dieses Ersatzschreiben (PR-Review-Finding, 4. Re-Review zu #976), WIRFT die
 * Funktion, statt weiterhin nur `false` zurückzugeben: das veraltete Item bleibt dann
 * unverändert und unbereinigt in der Queue liegen und würde beim nächsten Sync den
 * mittlerweile korrekten (neueren) Stand überschreiben - ein reines `false` wäre für den
 * Aufrufer nicht von "war ohnehin nichts zu tun" zu unterscheiden und würde diesen echten
 * Doppel-Fehlschlag stillschweigend verschlucken.
 *
 * Aus demselben Grund WIRFT die Funktion jetzt auch, wenn schon das initiale Lesen fehlschlägt
 * (PR-Review-Finding, 5. Re-Review zu #976): ein `getItem`-Fehler bedeutet "unbekannt, ob ein
 * veraltetes Item existiert", nicht "es existiert keins" - ein `false` hier wäre von "geprüft,
 * kein Item vorhanden" nicht zu unterscheiden gewesen. `false` bedeutet ab jetzt ausschließlich
 * "geprüft und sicher festgestellt: nichts zu tun" - jeder Fall, in dem das nicht sicher
 * feststeht, wirft. Aufrufer MÜSSEN einen Wurf hier als "Bereinigungsstatus unbekannt" werten
 * und den aktuellen Payload sicherheitshalber erneut vormerken (siehe AbschlussberichtWizard).
 *
 * Zwischen diesem `getItem` und dem `removeItem` unten kann kein `addQueueItem`-Aufruf für
 * dieselbe ID mehr physisch dazwischenlaufen (PR-Review-Finding, 6. Re-Review zu #976): seit
 * `withQueueItemLock`s Timeout den Lock nur noch für den AUFRUFER, nie mehr für nachfolgende
 * physische Operationen für dieselbe ID früher freigibt (siehe dortige Doku), sind `getItem` und
 * `removeItem` hier innerhalb desselben, ununterbrochenen `fn()`-Aufrufs wieder tatsächlich
 * atomar zueinander - eine frühere zusätzliche Compare-and-Swap-Prüfung direkt vor dem Entfernen
 * ist dadurch überflüssig geworden und wurde entfernt (sie hätte die eigentliche Lücke ohnehin
 * nicht geschlossen, weil Vergleich und Löschung weiterhin zwei getrennte IndexedDB-Operationen
 * gewesen wären).
 */
export const removeStaleQueueItem = async (
  id: string,
  isStale: (currentItem: QueueItem) => boolean,
  replacementItem: QueueItem
): Promise<boolean> =>
  withQueueItemLock(id, async () => {
    let current: QueueItem | null;
    try {
      current = await queueStore.getItem<QueueItem>(id);
    } catch (error) {
      console.error("Queue-Item konnte für die Bereinigung nicht gelesen werden:", id, error);
      throw error;
    }

    if (!current || !isStale(current)) {
      return false;
    }

    try {
      await queueStore.removeItem(id);
      return true;
    } catch (removeError) {
      console.error(
        "Veraltetes Queue-Item konnte nicht entfernt werden - versuche stattdessen, es mit dem aktuellen Stand zu überschreiben:",
        id,
        removeError
      );
      try {
        await queueStore.setItem(id, replacementItem);
        return false;
      } catch (replaceError) {
        console.error("Veraltetes Queue-Item konnte auch nicht überschrieben werden:", id, replaceError);
        throw replaceError;
      }
    }
  });

/**
 * Queue-Items verarbeiten mit Timeout pro Item
 */
// IDs von Queue-Items, deren Verarbeitung gerade läuft (zwischen Request-Start und
// -Ende). PR-Review-Finding zu #1090: ein Aufrufer, der ein Item per addToQueue/addQueueItem
// mit demselben queueKey ÜBERSCHREIBT (z.B. ReworkCommentThread.handleSaveEdit, das einen
// noch nicht synchronisierten Kommentar direkt am Queue-Eintrag bearbeitet), darf das nicht
// tun, während genau dieses Item schon mitten in einem laufenden Sync-Versuch ist — der
// bereits abgeschickte Request trägt noch die ALTE Payload, aber ein danach erfolgreicher
// Abschluss würde per removeQueueItem den (gerade erst überschriebenen, nie gesendeten)
// neuen Edit-Payload mitlöschen, ohne dass er je übertragen wurde. isQueueItemInFlight
// erlaubt Aufrufern, so einen Überschreib-Versuch VORHER abzulehnen, statt still Daten zu
// verlieren.
const inFlightItemIds = new Set<string>();

export const isQueueItemInFlight = (id: string): boolean => inFlightItemIds.has(id);

export const processQueue = async (
  items: QueueItem[]
): Promise<ProcessResult> => {
  const successful: string[] = [];
  const successfulItems: QueueItem[] = [];
  const failed: string[] = [];
  // Teilmenge von `failed`, die tatsächlich ENDGÜLTIG aus der Queue entfernt wurde
  // (Retries erschöpft, oder ein permanenter 4xx-Fehler) — im Unterschied zu einem Item,
  // das nach einem transienten Fehler (Netzwerk, 5xx, Timeout) mit erhöhtem Retry-Zähler
  // weiterhin in der Queue liegt und später erneut versucht wird. `failed` allein reicht
  // Aufrufern nicht, um zwischen beidem zu unterscheiden (PR-Review-Finding zu #1090:
  // ReworkCommentThread muss wissen, ob ein soeben enqueutes Item wirklich verloren ist,
  // bevor es den zugehörigen optimistischen Kommentar aus der UI entfernt — sonst
  // verschwindet ein Kommentar, der im Hintergrund später doch noch erfolgreich synct).
  const permanentlyFailed: string[] = [];
  // Volle Item-Referenzen (nicht nur IDs) für notifyQueueItemsPermanentlyFailed weiter unten —
  // Aufrufer außerhalb dieses Moduls kennen die ursprüngliche `data`-Payload sonst nicht mehr,
  // da das Item zu diesem Zeitpunkt bereits aus der Queue entfernt wurde.
  const permanentlyFailedItems: QueueItem[] = [];

  // Items, die das Retry-Limit erreicht haben, werden nicht mehr automatisch verarbeitet,
  // zählen aber als "failed" (statt spurlos aus successful/failed zu verschwinden) — der
  // User kann sie über den manuellen Sync (setzt retries zurück) erneut versuchen.
  const validItems = items.filter((item) => item.retries < 5);
  const exhaustedItems = items.filter((item) => item.retries >= 5);
  // #1231-Re-Review-Finding (P2): ein erschöpftes Item bleibt unverändert in IndexedDB liegen
  // (Retries werden hier nicht mehr weiter hochgezählt), landet also bei JEDEM weiteren
  // processQueue-Lauf (App-Neustart, online-Event) erneut in exhaustedItems - ohne diese
  // Markierung würde notifyQueueItemsPermanentlyFailed unten für dasselbe Item beliebig oft
  // denselben Toast auslösen. `permanentFailureNotified` wird direkt am Item persistiert,
  // sobald es einmal gemeldet wurde; ein manueller Sync/eine manuelle Bestätigung setzt dieses
  // Flag explizit mit zurück (siehe QueueContext.syncQueue/confirmPendingItem), nicht nur die
  // Retries (Re-Review-Finding, Anh360Volt: reines `retries: 0` allein hätte eine ERNEUTE
  // Erschöpfung nach dem manuellen Retry stumm gehalten).
  const newlyExhaustedItems = exhaustedItems.filter((item) => !item.data?.permanentFailureNotified);
  exhaustedItems.forEach((item) => {
    failed.push(item.id);
    permanentlyFailed.push(item.id);
  });
  permanentlyFailedItems.push(...newlyExhaustedItems);
  if (newlyExhaustedItems.length > 0) {
    // Promise.allSettled statt Promise.all (Re-Review-Finding, dennis360volt): dieser
    // Metadaten-Schreibfehler (z.B. IndexedDB-Quota) für EIN erschöpftes Item darf nicht dazu
    // führen, dass der gesamte processQueue-Aufruf ungefangen wirft und dadurch sämtliche
    // anderen, gültigen Items im selben Batch (validItems weiter unten) gar nicht erst
    // verarbeitet werden - die Benachrichtigung für dieses eine Item bleibt dann höchstens
    // erneut aus (harmlos, gleiche Situation wie ohne diesen Fix), andere Uploads laufen aber
    // unbeeinträchtigt weiter.
    const flagResults = await Promise.allSettled(
      newlyExhaustedItems.map((item) =>
        addQueueItem({ ...item, data: { ...item.data, permanentFailureNotified: true } })
      )
    );
    flagResults.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(
          `[queueService] permanentFailureNotified-Flag für Item ${newlyExhaustedItems[index].id} konnte nicht persistiert werden (Benachrichtigung könnte beim nächsten Lauf erneut ausgelöst werden):`,
          result.reason
        );
      }
    });
  }

  // Keine Items zu verarbeiten
  if (validItems.length === 0) {
    // Muss VOR diesem Return passieren, nicht erst am Ende der Funktion (Review-Finding
    // #1231 B): ein Item, das bei retries=4 transient scheitert, wird mit retries=5
    // persistiert und landet beim nächsten Lauf sofort in exhaustedItems. Ist es das
    // einzige Item im Batch, ist validItems leer — ohne diesen Aufruf hier bekäme der
    // `queue-item-permanently-failed`-Listener (App.tsx) den häufigsten
    // Erschöpfungs-Fall nie mit.
    notifyQueueItemsPermanentlyFailed(permanentlyFailedItems);
    return { successful, failed, permanentlyFailed };
  }

  // Offline-Erkennung
  if (!navigator.onLine) {
    validItems.forEach((item) => failed.push(item.id));
    notifyQueueItemsPermanentlyFailed(permanentlyFailedItems);
    return { successful, failed, permanentlyFailed };
  }

  // Items mit Promise.allSettled + Timeout verarbeiten
  const results = await Promise.allSettled(
    validItems.map(async (item) => {
      const controller = new AbortController();
      const deadline = timeoutPromise(30000, controller);
      inFlightItemIds.add(item.id);
      try {
        // 30s Timeout pro Upload — bricht bei Ablauf den zugrunde liegenden Request
        // via AbortController ab, statt ihn im Hintergrund weiterlaufen zu lassen.
        // resultData wird eingesammelt und unten an notifyProjectStatusRefresh
        // weitergereicht (PR-Review-Finding: liefert z.B. rework_comment_add die echte
        // Server-ID des erzeugten Kommentars, statt sie nachträglich per Inhalt zu raten).
        const resultData = await Promise.race([
          processQueueItem(item, controller.signal),
          deadline.promise,
        ]);
        deadline.cancel();
        return { success: true, id: item.id, permanent: false, resultData };
      } catch (error) {
        deadline.cancel();
        // Warten auf den ursprünglichen Monteur ist kein fehlgeschlagener Upload.
        // Insbesondere bei gemeinsam genutzten Geräten weder Zähler noch Item verändern.
        if (item.type === 'order_task_complete' && (error as { orderTaskAwaitingActor?: boolean })?.orderTaskAwaitingActor) {
          return { success: false, id: item.id, permanent: false };
        }
        console.error(`Fehler/Timeout bei Queue-Item ${item.id}:`, error);
        const status = (error as { status?: unknown })?.status;
        // Fachliche 403-Antworten dürfen keinen lokalen Login löschen. Nur eine
        // tatsächliche 401-Antwort weist auf eine ungültige Sitzung hin.
        if (status === 401) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        // Fehlendes Auth-Token ist ein Client-seitiges Timing-Problem (z.B. Token
        // beim Kaltstart noch nicht geladen), keine echte Server-Ablehnung — muss
        // wie ein transienter Fehler retried werden, nicht permanent verworfen.
        const isAuthMissing = (error as any)?.isAuthMissing === true;
        // Für HubSpot nur reparierbare Vertrags-/Konfliktfehler, Rate-Limits und
        // abgelaufene Sitzungen behalten. Fachliche 400/403/404 bleiben permanent.
        const propertyError = error as { isHubspotPropertyError?: boolean; code?: string };
        const isPropertyError = item.type === 'hubspot_text_property_update' || propertyError?.isHubspotPropertyError === true;
        const retryablePropertyError = isPropertyError && (
          status === 401 || status === 409 || status === 429 ||
          (status === 400 && propertyError?.code === 'IDEMPOTENCY_KEY_INVALID')
        );
        if (!retryablePropertyError && !isAuthMissing && typeof status === 'number' && status >= 400 && status < 500) {
          console.warn(`Queue-Item ${item.id} mit Status ${status} dauerhaft verworfen (kein Retry bei 4xx)`);
          await removeQueueItem(item.id);
          await removeQueueBlobs(item.type, item.data);
          return { success: false, id: item.id, permanent: true };
        }
        // Transiente Fehler (Netzwerk, Auth-Timing, 5xx, Timeout): Retry-Counter erhöhen.
        // item.data trägt für document_upload/image_upload seit dem Einreihen nur noch eine
        // Blob-Referenz statt eingebetteter Base64-Bilddaten (siehe extractQueueBlobs) - dieser
        // Retry schreibt daher nur noch die leichte Metadaten-Struktur neu, nicht mehr das
        // Foto/die Dateien selbst (#1216).
        const nextRetries = item.retries + 1;
        // #1231-Re-Review-Finding (Anh360Volt): der häufigste Übergang in die Erschöpfung
        // passiert GENAU HIER (retries 4→5 durch einen erneuten transienten Fehlschlag in
        // DIESEM Lauf), nicht erst wenn ein Item bereits mit retries>=5 in einen SPÄTEREN
        // processQueue-Aufruf geladen wird (siehe exhaustedItems/newlyExhaustedItems oben, die
        // nur diesen zweiten Fall abdecken). Ohne diese Behandlung bliebe `permanentlyFailed`
        // in DIESEM Lauf leer und notifyQueueItemsPermanentlyFailed würde nicht feuern - ohne
        // einen erneuten App-Neustart/Online-Event/manuellen Sync bekäme der Nutzer nie eine
        // Meldung, der Dokumentstatus bliebe fälschlich "completed".
        const justExhausted = nextRetries >= 5;
        const updatedItem = {
          ...item,
          retries: nextRetries,
          // Gleich beim Übergang in die Erschöpfung markieren (nicht erst beim nächsten
          // Lauf), damit ein nachfolgender manueller Sync (setzt retries+Flag zurück, siehe
          // QueueContext) diesen Lauf nicht fälschlich als bereits gemeldet überspringt, ein
          // App-Neustart davor aber auch kein zweites Mal denselben Toast auslöst.
          ...(justExhausted && { data: { ...item.data, permanentFailureNotified: true } }),
        };
        await addQueueItem(updatedItem);
        return { success: false, id: item.id, permanent: justExhausted };
      }
    })
  );

  // id -> resultData für erfolgreiche Items (siehe processQueueItem-Doku) — wird an
  // notifyProjectStatusRefresh weitergereicht, damit z.B. ReworkCommentThread die echte
  // Server-ID eines soeben erzeugten Kommentars aus dem project-status-refresh-Event lesen
  // kann, statt sie nachträglich per Inhalt (Text+Autor) zu erraten (PR-Review-Finding: diese
  // Zuordnung war nicht eindeutig, z.B. bei zwei gleichlautenden Kommentaren desselben Users).
  const resultDataById = new Map<string, unknown>();

  // Ergebnisse auswerten
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    const item = validItems[i];

    if (result.status === "fulfilled" && result.value.success) {
      successful.push(item.id);
      successfulItems.push(item);
      resultDataById.set(item.id, result.value.resultData);
      // Erfolgreiche Items aus IndexedDB entfernen
      await removeQueueItem(item.id);
      await removeQueueBlobs(item.type, item.data);
    } else {
      failed.push(item.id);
      // Promise.allSettled kann hier nur "rejected" liefern, wenn der try/catch oben selbst
      // wirft (z.B. removeQueueItem/addQueueItem schlägt fehl) — das ist kein fachlicher
      // 4xx-Fall, also konservativ NICHT als permanent werten (lieber unnötig weiter
      // beobachten lassen als ein Item fälschlich als endgültig verloren zu melden).
      if (result.status === "fulfilled" && result.value.permanent) {
        permanentlyFailed.push(item.id);
        permanentlyFailedItems.push(item);
      }
    }

    // Erst HIER als "nicht mehr in-flight" markieren, nicht schon beim Rückgabewert des
    // Requests oben — bis zu diesem Punkt ist der finale DB-Zustand für dieses Item
    // geschrieben (removeQueueItem bzw. bereits im try/catch oben für den Fehlerfall).
    // Ein früheres Löschen ließe ein kurzes Zeitfenster offen, in dem ein Überschreib-
    // Versuch (z.B. handleSaveEdit) fälschlich "sicher" erscheint, obwohl removeQueueItem
    // hier unten den gerade überschriebenen, nie gesendeten Edit noch mitlöschen würde.
    inFlightItemIds.delete(item.id);
  }

  // Background Sync registrieren, wenn Browser es unterstützt
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register("syncQueue");
    } catch (error) {
      console.error("Background Sync konnte nicht registriert werden:", error);
    }
  }

  notifyProjectStatusRefresh(successfulItems, resultDataById);
  notifyQueueItemsPermanentlyFailed(permanentlyFailedItems);
  notifyQueueItemsSyncFinished(validItems.map((item) => item.id));

  return { successful, failed, permanentlyFailed };
};

/**
 * Einzelnes Queue-Item verarbeiten (intern)
 */
// Rückgabetyp ist jetzt `Promise<unknown>` statt `Promise<void>` (PR-Review-Finding zu
// #1090): rework_comment_add gibt den erzeugten Kommentar zurück, alle anderen Typen liefern
// weiterhin `undefined` — siehe processReworkCommentAdd/processQueue für den Verwendungszweck.
async function processQueueItem(item: QueueItem, signal?: AbortSignal): Promise<unknown> {
  switch (item.type) {
    case "order_task_complete":
      return await processOrderTask(item.data as OrderTaskQueueData);

    case "image_upload":
      await processImageUpload(item, signal);
      break;

    case "document_upload":
      // Dokument-Upload (z.B. von PhaseScreen)
      await processDocumentUpload(item, signal);
      break;

    case "make_pdf_upload":
      await processMakePdfUpload(item);
      break;

    case "abschlussbericht_submit":
      await processAbschlussberichtSubmit(item);
      break;

    case "abschlussbericht_draft_upsert":
      await processAbschlussberichtDraftUpsert(item);
      break;

    case "maintenance_protocol_upsert":
      await processMaintenanceProtocolUpsert(item);
      break;

    case "maintenance_protocol_attachment":
      await processMaintenanceProtocolAttachment(item);
      break;

    case "maintenance_protocol_finalize":
      await processMaintenanceProtocolFinalize(item);
      break;

    case "projektdokumentation_upsert":
      await processProjektdokumentationUpsert(item);
      break;

    case "projektdokumentation_status_update":
      await processProjektdokumentationStatusUpdate(item);
      break;

    case "hubspot_text_property_update":
      await processHubspotTextPropertyUpdate(item);
      break;

    case "vaillant_serial_register":
      await processVaillantSerialRegister(item, signal);
      break;

    case "rework_comment_add":
      // Einziger Fall, der ein Ergebnis zurückgibt (siehe processReworkCommentAdd-Doku) —
      // return statt break, damit processQueue es als resultData einsammeln kann.
      return await processReworkCommentAdd(item);

    default:
      throw new Error(`Unbekannter Queue-Item-Typ: ${item.type}`);
  }
  return undefined;
}

/**
 * Formular-PDF verarbeiten
 *
 * Kontext:
 * - Das Formular wird im Modal ausgefüllt.
 * - Wenn offline: HTML wird in IndexedDB gequeued.
 * - Wenn online: Dieser Worker ruft die serverseitige PDF-Erzeugung auf und speichert fileId im Store.
 */
async function processMakePdfUpload(item: QueueItem): Promise<void> {
  const {
    dealId,
    documentTitle,
    htmlContent,
    filename,
    phaseId,
    documentId,
    pdfFields,
    signatures,
    signatureMissing,
    isPdfForm,
    formDataInputs,
  } = item.data || {};

  if (!filename || !documentTitle) {
    throw new Error("Unvollständige Make-PDF-Daten");
  }
  if (!dealId || !documentId) {
    throw new Error("dealId oder documentId fehlen für PDF-Erzeugung");
  }
  if (isPdfForm && !pdfFields) {
    throw new Error("VDZ: pdf_fields fehlen");
  }
  if (!isPdfForm && !htmlContent) {
    throw new Error("HTML Route: htmlContent fehlt");
  }

  // formDataInputs kommt als JSON-String an (siehe FormularModal.extractFormData) –
  // das Backend erwartet ein JSON-Objekt (JsonElement), daher parsen. Ein Parse-Fehler
  // muss den Upload fehlschlagen lassen (Retry über die Queue), sonst geht der
  // Formularstand für HubSpot stillschweigend verloren, obwohl das PDF erzeugt wurde.
  let parsedFormDataInputs: unknown;
  if (typeof formDataInputs === "string" && formDataInputs.trim()) {
    try {
      parsedFormDataInputs = JSON.parse(formDataInputs);
    } catch (e) {
      throw new Error(`formDataInputs konnte nicht geparst werden: ${e instanceof Error ? e.message : String(e)}`);
    }
  } else if (formDataInputs) {
    parsedFormDataInputs = formDataInputs;
  }

  const result = await generateProjectDocumentPdf(dealId, documentId, {
    documentTitle,
    fileName: filename,
    htmlContent: htmlContent || "",
    isPdfForm: isPdfForm ?? false,
    signatureMissing: signatureMissing ?? false,
    ...(isPdfForm && { pdfFields, signatures: signatures || {} }),
    ...(parsedFormDataInputs !== undefined && { formDataInputs: parsedFormDataInputs }),
  });

  const fileId = result?.fileId;
  if (!fileId) {
    throw new Error("PDF-Erzeugung lieferte keine fileId");
  }

  // Store-Update, damit die UI sofort "Anzeigen" anzeigen kann.
  if (phaseId && documentId) {
    const { saveDocumentFileId, updateDocumentStatus, saveDocumentData } =
      useDocumentStore.getState();

    saveDocumentFileId(phaseId, documentId, fileId);
    updateDocumentStatus(phaseId, documentId, "completed");

    // Optional: Signed URL direkt cachen (bessere UX beim "Anzeigen")
    try {
      const url = await getSignedUrl(fileId);
      if (url) saveDocumentData(phaseId, documentId, url);
    } catch (e) {
      console.warn("[queueService] Konnte Signed URL nicht cachen:", e);
    }
  }
}

/**
 * Hilfsfunktion: Dateiendung aus Data-URL ermitteln
 */
const getExtensionFromDataUrl = (dataUrl: string): string => {
  const mime = dataUrl.match(/:(.*?);/)?.[1];
  if (mime === "application/pdf") return ".pdf";
  if (mime?.startsWith("image/")) return ".jpg";
  return ".jpg"; // Default
};

const getExtensionFromFileName = (fileName?: string): string | null => {
  const match = fileName?.trim().match(/(\.[a-z0-9]{2,5})$/i);
  return match?.[1]?.toLowerCase() || null;
};

const getUploadFileName = (file: QueuedUploadFile, serialNumber?: string, documentId?: string): string => {
  const extension = isPhotoDataUrl(file.dataUrl)
    ? ".jpg"
    : getExtensionFromFileName(file.name) || getExtensionFromDataUrl(file.dataUrl);
  const baseName = serialNumber
    ? generateSerialNumberImageName(serialNumber)
    : documentId
      ? generateDocumentImageName(documentId)
      : generateUniqueImageName();
  return baseName.replace(".jpg", extension);
};

const normalizeQueuedPhotoFile = async (file: QueuedUploadFile): Promise<QueuedUploadFile> => {
  if (!isPhotoDataUrl(file.dataUrl) || isJpegPhotoDataUrl(file.dataUrl)) {
    return file;
  }

  const normalized = await normalizePhotoDataUrl(file.dataUrl, { fileName: file.name });
  return {
    ...file,
    name: normalized.fileName,
    type: normalized.contentType,
    size: normalized.size,
    dataUrl: normalized.dataUrl,
  };
};

const normalizeQueuedImageData = async (imageData: string): Promise<{ dataUrl: string; fileName: string }> => {
  if (isPhotoDataUrl(imageData) && !isJpegPhotoDataUrl(imageData)) {
    const normalized = await normalizePhotoDataUrl(imageData, { fileName: generateUniqueImageName() });
    return { dataUrl: normalized.dataUrl, fileName: normalized.fileName };
  }

  const fileName = generateUniqueImageName().replace(".jpg", getExtensionFromDataUrl(imageData));
  return { dataUrl: imageData, fileName };
};

const getDocumentUploadFiles = (item: QueueItem): QueuedUploadFile[] => {
  const files = Array.isArray(item.data?.files)
    ? item.data.files
        .filter((file: any) => typeof file?.dataUrl === "string" && file.dataUrl.trim().length > 0)
        .map((file: any) => ({
          id: file.id,
          name: file.name,
          type: file.type || file.mimeType,
          size: file.size,
          dataUrl: file.dataUrl,
        }))
    : [];

  if (files.length > 0) return files;
  if (typeof item.data?.imageData === "string" && item.data.imageData.trim().length > 0) {
    return [{ dataUrl: item.data.imageData }];
  }
  return [];
};

const appendUploadedDocumentFile = (
  phaseId: string,
  documentId: string,
  file: UploadedDocumentFile
): UploadedDocumentFile[] => {
  const { appendDocumentFiles } = useDocumentStore.getState();
  return appendDocumentFiles(phaseId, documentId, [file]);
};

/**
 * Bild-Upload verarbeiten – bevorzugt Google Drive, Fallback HubSpot
 */
async function processImageUpload(rawItem: QueueItem, signal?: AbortSignal): Promise<void> {
  // Hydriert eine LOKALE Kopie aus dem Blob-Store - rawItem (die Referenz, die processQueue im
  // Fehlerfall für den Retry zurückschreibt) bleibt unangetastet in der leichten
  // Blob-Referenz-Form, siehe hydrateQueueBlobs-Doku.
  const item: QueueItem = { ...rawItem, data: await hydrateQueueBlobs(rawItem.type, rawItem.data) };
  const { imageData, checklistItemId, phaseId, dealName, sharedDrive, gdriveFolderId } = item.data;

  const normalizedImage = await normalizeQueuedImageData(imageData);

  let fileId: string | null | void = null;

  if (dealName) {
    // Google Drive Upload: Ordnerstruktur = dealName / Dokumente / Phasenname
    const phaseDisplayName = resolvePhaseFolderName(phaseId);
    const driveResult = await uploadSingleFileAsync(
      normalizedImage.dataUrl,
      normalizedImage.fileName,
      dealName,
      ['Dokumente', phaseDisplayName],
      { sharedDrive, gdriveFolderId },
      signal,
    );
    if (driveResult) {
      const { saveDocumentFileId, saveDocumentData } = useDocumentStore.getState();
      saveDocumentFileId(phaseId, checklistItemId, driveResult.fileId);
      saveDocumentData(phaseId, checklistItemId, driveResult.webViewLink);
    }
    return;
  } else {
    // Fallback: HubSpot via Backend (wenn kein dealName übergeben)
    fileId = await uploadDocumentImage(phaseId, checklistItemId, normalizedImage.dataUrl, normalizedImage.fileName, signal);
  }

  if (fileId) {
    const { saveDocumentFileId } = useDocumentStore.getState();
    saveDocumentFileId(phaseId, checklistItemId, fileId);
    console.log(`[queueService] Drive FileId gespeichert: ${fileId} für ${checklistItemId}`);
  }
}

/**
 * Liest `DocumentUploadQueueData.pendingHubspotPatch` unabhängig davon, ob bereits (aktuelle
 * Form) ein Array oder noch (Altbestand aus vor dieser Änderung persistierten Queue-Items)
 * ein einzelnes Objekt in IndexedDB liegt - liefert immer ein neues Array, nie die Original-
 * Referenz, damit Aufrufer gefahrlos daraus entfernen/hineinschreiben können.
 */
function normalizePendingHubspotPatches(
  value: HubspotTextPropertyQueueData[] | HubspotTextPropertyQueueData | undefined,
): HubspotTextPropertyQueueData[] {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? [...value] : [value];
}

/**
 * Dokument-Upload verarbeiten (von PhaseScreen) – bevorzugt Google Drive, Fallback HubSpot
 */
async function processDocumentUpload(rawItem: QueueItem, signal?: AbortSignal): Promise<void> {
  // Hydriert eine LOKALE Kopie aus dem Blob-Store - rawItem (die Referenz, die processQueue im
  // Fehlerfall für den Retry zurückschreibt) bleibt unangetastet in der leichten
  // Blob-Referenz-Form, siehe hydrateQueueBlobs-Doku.
  const item: QueueItem = { ...rawItem, data: await hydrateQueueBlobs(rawItem.type, rawItem.data) };
  const { documentId, phaseId, dealName, sharedDrive, gdriveFolderId, serialNumber, pendingHubspotPatch }: DocumentUploadQueueData = item.data;
  const uploadFiles = getDocumentUploadFiles(item);
  // Vorgezogen aus dem HubSpot-Patch-Block weiter unten, damit auch der Drive-Upload selbst die
  // dealId kennt (PR-Review-Finding, Ops-App-PR #9/one360-api-PR #475/#476): Dokumenttypen ohne
  // eigene HubSpot-Property (skipHubspotPatch, z.B. "aussenfuehler") brauchen sie dort, um serverseitig
  // eine dauerhafte, projektbezogene Bestätigung abzulegen statt nur die Datei abzulegen.
  const dealId = phaseId.split('-')[0];

  if (!documentId || !phaseId) {
    throw new Error("Unvollständige Dokument-Upload-Daten");
  }

  // #1231-Review-Finding (Anh360Volt, Re-Review zu PR #428): ausstehende HubSpot-Patches eines
  // VORHERIGEN Teil-Erfolgs (Foto A bereits in Drive, Live-Patch + dessen eigenständiges
  // Retry-Item-Enqueue gescheitert) müssen VOR weiteren Datei-Uploads nachgeholt werden, nicht
  // erst wenn uploadFiles komplett leer ist - sonst verarbeitet ein Retry zuerst die noch
  // ausstehende Datei B; scheitert B mit einem permanenten 4xx-Fehler, entfernt processQueue
  // das GESAMTE Item (inkl. des längst in Drive liegenden, aber nie gepatchten Fotos A) aus
  // der Queue, ohne dass der A-Patch je nachgeholt wurde. Läuft über dieselbe
  // processHubspotTextPropertyUpdate-Logik (inkl. Multi-Upload-Merge), die auch das separate
  // hubspot_text_property_update-Item verwenden würde. Bereits erfolgreich nachgeholte Patches
  // werden sofort aus der Liste entfernt (statt erst am Ende), damit ein späterer Patch in
  // derselben Liste, der wirft, nicht dazu führt, dass ein Retry bereits erledigte Patches ein
  // zweites Mal gegen HubSpot schreibt.
  const remainingPatches = normalizePendingHubspotPatches(pendingHubspotPatch);
  while (remainingPatches.length > 0) {
    const patch = remainingPatches[0];
    await processHubspotTextPropertyUpdate({ ...item, data: patch });
    remainingPatches.shift();
    rawItem.data.pendingHubspotPatch = remainingPatches.length > 0 ? [...remainingPatches] : undefined;
  }

  if (uploadFiles.length === 0) {
    // #1231-Review-Finding (dennis360volt, Re-Review zu PR #428): dieses Item kann bereits
    // VOLLSTÄNDIG hochgeladen sein (alle Fotos in Drive, alle Patches oben gerade nachgeholt
    // oder ohnehin keine offen), aber noch nicht final aus der Queue entfernt - z.B. wenn die
    // App/der Tab zwischen dem durablen Persist nach dem letzten Foto (s.u., `allFilesUploaded`)
    // und processQueue's abschließendem removeQueueItem beendet wurde. Ohne diese Markierung
    // würde ein Neustart diesen Zustand fälschlich als unvollständige/fehlerhafte Daten werten,
    // die Retries erschöpfen und den (tatsächlich längst erfolgreichen) Dokumentstatus auf
    // "pending" zurücksetzen.
    if (rawItem.data?.allFilesUploaded) {
      return;
    }
    throw new Error("Unvollständige Dokument-Upload-Daten");
  }

  const existingFiles = normalizeUploadedDocumentFiles(item.data?.existingFiles);
  if (existingFiles.length > 0) {
    const { appendDocumentFiles } = useDocumentStore.getState();
    appendDocumentFiles(phaseId, documentId, existingFiles);
  }

  const docConfig = getDocumentTypeConfigById(documentId);

  // Blob-Referenzen im ORIGINAL-Item (rawItem, das processQueue bei einem Retry unverändert
  // zurückschreibt - siehe hydrateQueueBlobs-Doku oben) - nach erfolgreicher Hydration in
  // derselben Reihenfolge/Länge wie uploadFiles (hydrateQueueBlobs bildet 1:1 ab, ohne zu
  // filtern). Analog zum bestehenden Dedup-Pattern in processMaintenanceProtocolAttachment:
  // fehlt dieser Schutz, lädt ein Retry nach einem Teil-Fehlschlag eines Multi-Foto-Batches
  // bereits erfolgreich hochgeladene Fotos erneut hoch - mit neuer UUID im Dateinamen, also als
  // physisches Duplikat in Drive/HubSpot (ADO #1231, Kriterium 6).
  const rawFiles = Array.isArray(rawItem.data?.files) && rawItem.data.files.length === uploadFiles.length
    ? rawItem.data.files
    : undefined;

  for (const [uploadIndex, queuedFile] of uploadFiles.entries()) {
    const uploadFile = await normalizeQueuedPhotoFile(queuedFile);
    const fileName = getUploadFileName(uploadFile, serialNumber, documentId);
    let fileId: string | null | void = null;
    // Nur true, wenn der Upload nachweislich (mit fileId) bestätigt wurde - steuert unten, ob
    // der Blob-Cleanup + rawItem.data.files-Kürzung ausgeführt werden darf (#1231-Review-
    // Finding C, dev-Variante: der Cleanup lief bisher auch bei driveResult===null/undefined,
    // dadurch wurde ein NICHT bestätigter Upload trotzdem als erledigt markiert und der Blob
    // beim Retry unwiederbringlich gelöscht).
    let uploadConfirmed = false;

    if (dealName) {
      // Google Drive Upload: Ordnerstruktur = dealName / Dokumente / Phasenname
      const phaseDisplayName = resolvePhaseFolderName(phaseId);
      const driveResult = await uploadSingleFileAsync(
        uploadFile.dataUrl,
        fileName,
        dealName,
        ['Dokumente', phaseDisplayName],
        { sharedDrive, gdriveFolderId },
        signal,
        dealId,
      );
      if (driveResult) {
        uploadConfirmed = true;
        const { saveDocumentData, updateDocumentStatus } = useDocumentStore.getState();
        const mergedFiles = appendUploadedDocumentFile(phaseId, documentId, {
          id: driveResult.fileId,
          fileId: driveResult.fileId,
          url: driveResult.webViewLink,
          // Nutzer-Rückmeldung: driveResult.fileName ist der ECHTE, von Drive vergebene Name
          // (das Backend bestätigt ihn in der Upload-Antwort) — nicht der hier lokal generierte
          // Platzhalter (`fileName`, s. getUploadFileName) oder ein evtl. veralteter lokaler
          // Name. Sonst weicht ein später erzeugter "-markiert"-Dateiname vom tatsächlichen
          // Original-Namen in Drive ab.
          name: driveResult.fileName || uploadFile.name || fileName,
          mimeType: uploadFile.type,
          uploadedAt: new Date().toISOString(),
          thumbnailUrl: driveResult.thumbnailLink,
          previewData: uploadFile.dataUrl,
          source: "drive",
        });
        saveDocumentData(phaseId, documentId, driveResult.webViewLink);
        updateDocumentStatus(phaseId, documentId, "completed");

        // Drive-Link nach HubSpot patchen (außer Dokumenttypen mit eigenem Flow, z.B. Seriennummer)
        if (!docConfig?.skipHubspotPatch) {
          const hubspotProperty = DOCUMENT_TO_PROPERTY_MAP[documentId];
          if (dealId && hubspotProperty && driveResult.webViewLink) {
            // Multi-Upload-Dokumente patchen ein JSON-Array aller Dateien, Einzel-Upload-
            // Dokumente patchen nur den Drive-Link — gesteuert über die Dokumenttyp-
            // Konfiguration statt über hartkodierte Phasen-/ID-Sonderfälle.
            const value = docConfig?.multiUpload
              ? uploadedDocumentFilesToPropertyValue(mergedFiles)
              : driveResult.webViewLink;
            try {
              await updateDealTextProperty(dealId, hubspotProperty, value, driveResult.fileId);
            } catch (e) {
              // Der Drive-Upload ist bereits erfolgreich und darf beim Retry nicht erneut
              // hochgeladen werden — statt den Fehler zu verschlucken (Datei landet in
              // Drive, aber nie im HubSpot-Deal), wird der Property-Patch als eigenes,
              // unabhängig retry-fähiges Queue-Item eingereiht.
              console.warn('[queueService] HubSpot Drive-Link Patch fehlgeschlagen, wird als eigenes Retry-Item eingereiht:', e);
              const patchRetryData: HubspotTextPropertyQueueData = {
                idempotencyKey: driveResult.fileId,
                dealId,
                property: hubspotProperty,
                value,
                // Multi-Upload-Property: Retry liest bei der Verarbeitung den dann
                // aktuellen Property-Wert und merged nur die neue Datei hinein,
                // statt diesen Snapshot blind zu überschreiben (siehe processHubspotTextPropertyUpdate).
                ...(docConfig?.multiUpload && {
                  multiUpload: true,
                  newFiles: normalizeUploadedDocumentFiles({
                    id: driveResult.fileId,
                    fileId: driveResult.fileId,
                    url: driveResult.webViewLink,
                    name: driveResult.fileName || uploadFile.name || fileName,
                    mimeType: uploadFile.type,
                    thumbnailUrl: driveResult.thumbnailLink,
                  }),
                }),
              };
              // #1231-Re-Review-Finding: am ORIGINAL-Item (rawItem) hinterlegen, BEVOR das
              // separate Retry-Item eingereiht wird — wirft addQueueItem hier selbst (z.B.
              // IndexedDB-Quota), bleibt der Patch trotzdem über die normalen Retries dieses
              // Items erreichbar (s. Abfrage am Funktionsanfang), statt spurlos verloren zu
              // gehen. Muss auf rawItem geschrieben werden, nicht auf die hydrierte `item`-
              // Kopie: nur rawItem ist die Referenz, die processQueue bei einem Retry
              // zurückschreibt (siehe hydrateQueueBlobs-Doku oben). War das separate Item
              // erfolgreich eingereiht, wird dieser Fallback-Zustand direkt wieder entfernt,
              // damit der Patch nicht doppelt läuft.
              //
              // An die bestehende Liste ANHÄNGEN statt sie zu überschreiben (#1231-Review-
              // Finding, Re-Review): bei einem Multi-Foto-Batch kann derselbe Enqueue-Fehler
              // bereits für ein früheres Foto in dieser Schleife aufgetreten sein - ein
              // einzelner Slot hätte dessen noch offenen Patch hier stillschweigend verloren.
              const pendingPatchesForThisFile = normalizePendingHubspotPatches(rawItem.data.pendingHubspotPatch);
              pendingPatchesForThisFile.push(patchRetryData);
              rawItem.data.pendingHubspotPatch = pendingPatchesForThisFile;
              try {
                await addQueueItem({
                  id: `hubspot-patch-retry-${dealId}-${hubspotProperty}-${Date.now()}`,
                  type: "hubspot_text_property_update",
                  data: patchRetryData,
                  retries: 0,
                  createdAt: new Date(),
                });
                // Nur DIESEN soeben erfolgreich eingereihten Patch wieder entfernen - nicht die
                // gesamte Liste, die evtl. noch für andere Fotos desselben Batches offene
                // Patches enthält.
                const stillPending = pendingPatchesForThisFile.filter((patch) => patch !== patchRetryData);
                rawItem.data.pendingHubspotPatch = stillPending.length > 0 ? stillPending : undefined;
              } catch (enqueueError) {
                console.warn('[queueService] HubSpot-Patch-Retry-Item konnte nicht eingereiht werden, bleibt am document_upload-Item hinterlegt:', enqueueError);
              }
            }
          }
        }
      }
    } else {
      // Fallback: HubSpot via Backend
      fileId = await uploadDocumentImage(phaseId, documentId, uploadFile.dataUrl, fileName, signal);

      if (fileId) {
        uploadConfirmed = true;
        const { updateDocumentStatus } = useDocumentStore.getState();
        const uploadedFile = normalizeUploadedDocumentFiles(fileId)[0];
        if (uploadedFile) {
          appendUploadedDocumentFile(phaseId, documentId, {
            ...uploadedFile,
            name: uploadFile.name || fileName,
            mimeType: uploadFile.type,
            previewData: uploadFile.dataUrl,
          });
        }
        updateDocumentStatus(phaseId, documentId, "completed");
        console.log(`[queueService] FileId gespeichert: ${fileId} für ${documentId}`);
      }
    }

    // Ein Upload ohne bestätigte fileId (2xx-Antwort ohne fileId, siehe uploadSingleFileAsync-
    // Vertrag) darf NICHT als erledigt durchlaufen - sonst würde der Blob unten gelöscht und die
    // Datei aus rawItem.data.files gekürzt, obwohl sie nie nachweislich gespeichert wurde
    // (#1231-Review-Finding C, dev-Variante). Werfen statt still weiterlaufen lassen, damit der
    // reguläre Retry-Mechanismus (processQueue-Catch, erhöht nur `retries`) greift.
    if (!uploadConfirmed) {
      throw new Error(
        `Upload für Datei ${uploadIndex + 1}/${uploadFiles.length} (documentId=${documentId}) lieferte keine bestätigte fileId`,
      );
    }

    // Nach jedem erfolgreichen Einzel-Upload bleibt für einen späteren Fehlschlag nur der Rest
    // in rawItem.data - ein Retry (schreibt rawItem zurück, siehe processQueue) kann dadurch
    // keine bereits hochgeladene Datei erneut hochladen (ADO #1231, Kriterium 6).
    if (rawFiles) {
      rawItem.data.files = rawFiles.slice(uploadIndex + 1);
      if (rawItem.data.files.length === 0) {
        // Markiert diesen vollständigen Abschluss explizit (s. Guard am Funktionsanfang) -
        // unterscheidet ihn von einem genuinely unvollständigen/fehlerhaft eingereihten Item,
        // das ebenfalls mit einer leeren/fehlenden files-Liste hier ankäme.
        rawItem.data.allFilesUploaded = true;
      }
      // Gekürzten Fortschritt DURABEL persistieren, BEVOR der zugehörige Blob gelöscht wird
      // (#1231-Review-Finding, Re-Review): bisher lief die Kürzung nur im Speicher, geschrieben
      // wurde rawItem erst am Ende von processQueue (bei Erfolg oder einem nachgelagerten
      // Fehler). Wird die App/der Tab dazwischen beendet - z.B. nach diesem Foto, aber
      // während des NÄCHSTEN noch laufenden Uploads - stand in IndexedDB weiterhin die alte,
      // ungekürzte files-Liste inkl. der Referenz auf den unten gelöschten Blob. Der nächste
      // Start scheiterte dann in hydrateQueueBlobs mit "Queue-Blob nicht gefunden", bevor die
      // übrigen Fotos überhaupt versucht werden konnten - auch ein manueller Retry half nicht.
      await addQueueItem(rawItem);
      // Den zugehörigen Blob SOFORT aufräumen, nicht erst wenn das gesamte Item (alle Dateien)
      // erfolgreich ist - sonst würde die zentrale Aufräumung in processQueue
      // (removeQueueBlobs(item.type, item.data)) beim finalen Erfolg nur noch die zuletzt
      // verbliebenen Dateien in `files` sehen (der Rest wurde ja bereits oben weggeschnitten)
      // und die Blobs der schon früher hochgeladenen Dateien blieben für immer verwaist im
      // Blob-Store liegen.
      const uploadedBlobRef = (rawFiles[uploadIndex] as QueuedUploadFileRef | undefined)?.blobRef;
      if (typeof uploadedBlobRef === "string") {
        await queueBlobStore.removeItem(uploadedBlobRef).catch((err) => {
          console.error("[queueService] Bereits hochgeladener Queue-Blob konnte nicht aufgeräumt werden:", err);
        });
      }
    }
  }

  // #1231-Re-Review-Finding: Konnte der HubSpot-Patch-Retry oben nicht als eigenes Item
  // eingereiht werden, hängt er jetzt als `pendingHubspotPatch` am rawItem selbst. Ohne diesen
  // Throw würde processDocumentUpload hier normal zurückkehren (alle Fotos SIND ja erfolgreich
  // in Drive) - processQueue wertet das als vollen Erfolg und entfernt das Item per
  // removeQueueItem komplett aus der Queue, womit der ausstehende Patch nie mehr nachgeholt
  // würde. Ein Throw hier hält das Item mit erhöhtem Retry-Zähler in der Queue, sodass der
  // Guard am Funktionsanfang den Patch beim nächsten Versuch erneut aufgreift.
  if (rawItem.data?.pendingHubspotPatch) {
    throw new Error("HubSpot-Patch konnte nicht eingereiht werden, wird als transienter Fehler erneut versucht");
  }
}

async function processHubspotTextPropertyUpdate(item: QueueItem): Promise<void> {
  const { dealId, property, value, multiUpload, newFiles, idempotencyKey }: HubspotTextPropertyQueueData = item.data;

  if (!dealId || !property) {
    throw new Error("Unvollständige HubSpot-Property-Update-Daten");
  }

  if (multiUpload) {
    await withHubspotPropertyLock(`${dealId}:${property}`, async () => {
      // Aktuellen Property-Wert frisch lesen statt den zum Enqueue-Zeitpunkt
      // berechneten Snapshot zu schreiben — zwischenzeitlich von anderen Uploads
      // hinzugefügte Dateien bleiben so erhalten (siehe HubspotTextPropertyQueueData).
      const currentDeal = await getDealByIdAsync(dealId, { bypassCache: true });
      // getDealByIdAsync schluckt API-/Netzwerkfehler und liefert dann `null` zurück.
      // Würde das hier stillschweigend als "keine bestehenden Dateien" gewertet, würde
      // der PATCH nur die neue Datei schreiben und bestehende Dateien im Property löschen.
      // Stattdessen wird geworfen, damit das Queue-Item retry-fähig bleibt.
      if (!currentDeal) {
        throw new Error(`HubSpot-Deal ${dealId} konnte für Multi-Upload-Retry nicht frisch geladen werden`);
      }
      const currentFiles = normalizeUploadedDocumentFiles((currentDeal as unknown as Record<string, unknown>)[property]);
      const mergedFiles = mergeUploadedDocumentFiles(currentFiles, newFiles);
      await updateDealTextProperty(dealId, property, uploadedDocumentFilesToPropertyValue(mergedFiles), idempotencyKey ?? item.id);
    });
    return;
  }

  await updateDealTextProperty(dealId, property, value, idempotencyKey ?? item.id);
}

/**
 * Ermittelt aus einer fachlichen Vaillant-Ablehnung (`success: false`), ob sie retry-fähig
 * ist. Nur eine ausdrücklich transiente Angabe in `statusCode` (5xx: Vaillant/der Proxy war
 * gerade nicht erreichbar; 429: Rate-Limit) gilt als retry-fähig — alles andere, insbesondere
 * auch `statusCode: 200` (die äußere Antwort war ok, nur inhaltlich abgelehnt) oder ein
 * beliebiger 4xx-Wert, ist eine permanente, nicht retry-fähige fachliche Ablehnung (z.B.
 * ungültige Seriennummer) und wird durch Wiederholen nicht anders ausgehen.
 *
 * Code-Review-Finding (Re-Review): die erste Fassung hat `statusCode` unverändert als
 * `.status` durchgereicht. Für `statusCode: 200` — laut bisherigem Response-Vertrag und dem
 * ursprünglichen Regressionstest der Normalfall für eine ungültige Seriennummer — liegt das
 * außerhalb des von `processQueue` geprüften 4xx-Bereichs und wäre fälschlich als transient
 * behandelt worden, das Item also endlos retried worden.
 *
 * Gibt `undefined` zurück, wenn transient (kein `.status` → processQueue behandelt es wie
 * jeden anderen Fehler ohne numerischen Status: transient, Retry-Zähler erhöhen), sonst den
 * festen permanenten Code 422.
 */
function classifyVaillantRejectionStatus(statusCode: unknown): number | undefined {
  const isTransient = typeof statusCode === 'number' && (statusCode >= 500 || statusCode === 429);
  return isTransient ? undefined : 422;
}

/**
 * Vaillant-Seriennummer-Registrierung nachholen (#1199).
 *
 * Fallback für PhaseScreen.handleSeriennummerRegistrierenSubmit — wird nur bei einem
 * Netzwerkfehler/Timeout/5xx enqueued, nie bei einer fachlichen Ablehnung durch Vaillant
 * (siehe unten). Holt bei Erfolg exakt die zwei Folgeschritte nach, die der Live-Pfad in
 * PhaseScreen sonst auch ausführt: Consent-Timestamp in HubSpot schreiben und den
 * Dokumentstatus lokal auf "completed" setzen — sonst bliebe das Dokument nach einem
 * nachgeholten Retry fälschlich als offen stehen, obwohl die Registrierung durch ist.
 */
async function processVaillantSerialRegister(item: QueueItem, signal?: AbortSignal): Promise<void> {
  const { dealId, serialNumber, contactEmail, inbetriebnahmePhaseId, idempotencyKey }: VaillantSerialRegisterQueueData = item.data || {};

  if (!dealId || !serialNumber || !inbetriebnahmePhaseId) {
    throw new Error("Unvollständige Vaillant-Registrierungs-Daten");
  }

  // Derselbe Key wie beim ursprünglichen Live-Versuch (#1202) - die One360-API dedupliziert
  // serverseitig darauf, ein Retry hier kontaktiert Vaillant bei einer bereits erfolgreichen
  // vorherigen Registrierung also NICHT erneut.
  const data = await registerVaillantSerialNumber(serialNumber, contactEmail, signal, idempotencyKey);
  if (!data.success) {
    const detail =
      typeof data.response === 'object' && data.response !== null && 'detail' in data.response
        ? String((data.response as { detail?: unknown }).detail ?? 'Ungültige Seriennummer')
        : 'Ungültige Seriennummer';
    const rejectionStatus = classifyVaillantRejectionStatus(data.statusCode);
    throw Object.assign(
      new Error(`Registrierung fehlgeschlagen: ${detail}`),
      rejectionStatus !== undefined ? { status: rejectionStatus } : {},
    );
  }

  // Ab hier ist die Registrierung bei Vaillant unwiderruflich passiert (der Kunde bekommt
  // eine Bestätigungsmail) — ein Fehler in den Folgeschritten darf deshalb NICHT dazu führen,
  // dass processQueue dieses Item erneut versucht (das würde registerVaillantSerialNumber
  // ein zweites Mal aufrufen und eine zweite Registrierung/Bestätigungsmail auslösen,
  // Code-Review-Finding). Der Consent-Schreibvorgang wird deshalb bei einem Fehler separat
  // und unabhängig als reines HubSpot-Property-Update nachgezogen, niemals durch einen
  // Retry dieses Items.
  const consentTimestamp = new Date().toISOString();
  // Code-Review-Finding (Re-Review): schlagen SOWOHL der Live-PATCH ALS AUCH das Nachziehen
  // des separaten Consent-Retry-Items fehl, darf das nicht still verworfen werden (der Test
  // hätte das sonst als Erfolg festgeschrieben) - der Consent-Vermerk ist dann unwiederbringlich
  // verloren, ohne dass irgendwo eine Retry-Möglichkeit bestehen bleibt. Am Ende der Funktion
  // wird dieser Fall deshalb als permanenter Fehler geworfen (siehe unten).
  let consentPermanentlyLost = false;
  try {
    await updateDealTextProperty(dealId, 'vaillant_api_consent_angefragt', consentTimestamp, `consent:${item.id}`);
  } catch (consentError) {
    console.error('[VaillantSerialRegister] Consent-Update fehlgeschlagen, wird separat nachgeholt:', consentError);
    try {
      await addQueueItem({
        id: `vaillant-consent-retry:${dealId}:${item.id}`,
        type: 'hubspot_text_property_update',
        data: {
          dealId,
          property: 'vaillant_api_consent_angefragt',
          idempotencyKey: `consent:${item.id}`,
          value: consentTimestamp,
        } satisfies HubspotTextPropertyQueueData,
        retries: 0,
        createdAt: new Date(),
      });
    } catch (queueError) {
      // Darf NICHT sofort weitergeworfen werden - ein Reject hier (z.B. Speicherplatz voll)
      // würde diese Funktion sonst VOR den Erfolgs-Seiteneffekten unten abbrechen lassen und
      // processQueue das ursprüngliche vaillant_serial_register-Item für einen Retry behalten,
      // der beim nächsten Sync-Versuch registerVaillantSerialNumber ein zweites Mal aufrufen
      // würde. Stattdessen wird unten geworfen, NACHDEM Dokumentstatus/lokale Caches bereits
      // aktualisiert sind — das Item gilt dann korrekt als permanentlyFailed (kein Retry, keine
      // erneute Registrierung), statt fälschlich als successful durchzugehen.
      console.error('[VaillantSerialRegister] Consent-Retry konnte nicht in die Queue eingetragen werden:', queueError);
      consentPermanentlyLost = true;
    }
  }

  const { saveDocumentData, updateDocumentStatus } = useDocumentStore.getState();
  // Code-Review-Finding (PR #431/#432): siehe PhaseScreen.performSeriennummerRegistrierenSubmit -
  // derselbe separate Schlüssel wie dort, statt die bestätigte Seriennummer unter
  // 'seriennummer-registrieren-text' mit dem Consent-Zeitstempel zu überschreiben.
  saveDocumentData(inbetriebnahmePhaseId, 'seriennummer-registrieren-consent-text', consentTimestamp);
  updateDocumentStatus(inbetriebnahmePhaseId, 'seriennummer-registrieren', 'completed');

  const cachedDeal = useDealsStore.getState().deals.find((d) => d.id === dealId);
  if (cachedDeal) {
    useDealsStore.getState().upsertDeal({ ...cachedDeal, vaillant_api_consent_angefragt: consentTimestamp });
  }

  if (consentPermanentlyLost) {
    // Registrierung ist erfolgreich und bleibt es (Dokumentstatus oben bereits "completed") -
    // aber der Consent-Vermerk ist weder in HubSpot noch in der Queue vorhanden. Als
    // permanenten (4xx-artigen) Fehler werfen, damit processQueue dieses Item NICHT erneut
    // versucht (removeQueueItem, kein Retry) und es sichtbar in permanentlyFailed landet,
    // statt still als successful durchzugehen.
    throw Object.assign(
      new Error('Vaillant-Consent-Vermerk konnte nicht gespeichert werden.'),
      { status: 422 },
    );
  }
}

// Gibt den erzeugten Kommentar (inkl. echter Server-ID) zurück, statt ihn wie zuvor zu
// verwerfen — PR-Review-Finding: eine nachträgliche Zuordnung "welcher frisch geladene
// Kommentar gehört zu dieser tempId" per Text+Autor ist nicht eindeutig (z.B. zwei
// gleichlautende Kommentare desselben Ops-Users). Die echte ID muss stattdessen direkt aus
// der POST-Antwort stammen; sie wird über processQueueItem -> processQueue ->
// notifyProjectStatusRefresh bis zum project-status-refresh-Event durchgereicht (siehe
// ReworkCommentThread.confirmSuccess).
async function processReworkCommentAdd(item: QueueItem): Promise<import('../types/hubspot').ReworkComment> {
  const { ticketId, text, mentionedOwnerIds }: ReworkCommentAddQueueData = item.data || {};

  if (!ticketId || !text?.trim()) {
    throw new Error("Unvollständige Nacharbeiten-Kommentar-Daten");
  }

  return await addReworkCommentAsync(ticketId, { text, mentionedOwnerIds: mentionedOwnerIds ?? [] });
}

async function processAbschlussberichtSubmit(item: QueueItem): Promise<void> {
  const {
    projectId,
    phaseId,
    submission,
  }: {
    projectId?: string;
    phaseId?: string;
    submission?: AbschlussberichtSubmission;
  } = item.data || {};

  if (!projectId || !phaseId || !submission) {
    throw new Error("Unvollständige Abschlussbericht-Daten");
  }

  await submitAbschlussbericht(projectId, submission);

  const { updatePhaseStatus } = useDocumentStore.getState();
  updatePhaseStatus(phaseId, "completed", submission.submittedAt, submission.submittedByEmail);
}

async function processAbschlussberichtDraftUpsert(item: QueueItem): Promise<void> {
  const {
    projectId,
    draft,
  }: {
    projectId?: string;
    draft?: AbschlussberichtDraft;
  } = item.data || {};

  if (!projectId || !draft) {
    throw new Error("Unvollständige Abschlussbericht-Draft-Daten");
  }

  await upsertAbschlussberichtDraft(projectId, draft);
}

async function processMaintenanceProtocolUpsert(item: QueueItem): Promise<void> {
  const { projectId, protocolId, payload }: {
    projectId?: string;
    protocolId?: string;
    payload?: MaintenanceProtocolUpsertPayload;
  } = item.data || {};
  if (!projectId || !protocolId || !payload) {
    throw new Error("Unvollständige Serviceprotokoll-Daten");
  }
  await upsertMaintenanceProtocol(projectId, protocolId, payload);
}

async function processMaintenanceProtocolAttachment(item: QueueItem): Promise<void> {
  const data = item.data as Partial<MaintenanceProtocolAttachmentQueueData> | undefined;
  const { projectId, protocolId, dealName, files, payload } = data ?? {};
  if (!projectId || !protocolId || !files?.length) {
    throw new Error("Unvollständige Serviceanhang-Daten");
  }
  // Ältere Queue-Einträge können noch ohne Dealname vorliegen. Die Projekt-ID ist
  // als Fallback zugleich ein stabiler, gültiger Name für die Drive-Ordnerauflösung.
  const storageDealName = dealName?.trim() || projectId;
  if (payload) await upsertMaintenanceProtocol(projectId, protocolId, payload);
  for (const [index, file] of files.entries()) {
    await uploadMaintenanceAttachment(
      projectId,
      protocolId,
      storageDealName,
      dataUrlToFile(file.dataUrl, file.name || "serviceanhang"),
    );
    // Nach jedem erfolgreichen Upload bleibt für einen Fehler nur der Rest in
    // der Queue. Ein Retry kann dadurch keine bereits abgelegten Dateien duplizieren.
    if (data) data.files = files.slice(index + 1);
  }
}

async function processMaintenanceProtocolFinalize(item: QueueItem): Promise<void> {
  const data = item.data as Partial<MaintenanceProtocolFinalizeQueueData> | undefined;
  const { projectId, protocolId, dealName, projectDetails, payload } = data ?? {};
  if (!projectId || !protocolId) {
    throw new Error("Unvollständige Finalisierungsdaten für das Serviceprotokoll");
  }
  const storageDealName = dealName?.trim() || projectId;
  if (payload) await upsertMaintenanceProtocol(projectId, protocolId, payload);
  await finalizeMaintenanceProtocol(projectId, protocolId, storageDealName, projectDetails);
}

async function processProjektdokumentationUpsert(item: QueueItem): Promise<void> {
  const {
    projectId,
    payload,
  }: {
    projectId?: string;
    payload?: ProjektdokumentationFormData;
  } = item.data || {};

  if (!projectId || !payload) {
    throw new Error("Unvollständige Projektdokumentation-Daten");
  }

  await upsertProjektdokumentation(projectId, payload);
}

async function processProjektdokumentationStatusUpdate(item: QueueItem): Promise<void> {
  const {
    projectId,
    phaseId,
    payload,
  }: {
    projectId?: string;
    phaseId?: string;
    payload?: ProjektdokumentationStatusUpdate;
  } = item.data || {};

  if (!projectId || !payload) {
    throw new Error("Unvollständige Projektdokumentation-Statusdaten");
  }

  const record = await apiUpdateProjektdokumentationStatus(projectId, payload);

  const existingDeal = useDealsStore.getState().deals.find((deal) => deal.id === projectId);
  if (existingDeal) {
    const existingSnapshot = existingDeal.project_status || null;
    const nextSnapshot = existingSnapshot ? { ...existingSnapshot } : null;
    if (nextSnapshot) {
      nextSnapshot.projektdokumentationApprovalStatus =
        record.status || nextSnapshot.projektdokumentationApprovalStatus || null;
    }
    useDealsStore.getState().upsertDeal({
      ...existingDeal,
      project_status: nextSnapshot,
    });
  }

  if (payload.status === "approved" && phaseId) {
    const approvedAt = record.approvedAt || new Date().toISOString();
    useDocumentStore
      .getState()
      .updatePhaseStatus(
        phaseId,
        "completed",
        approvedAt,
        record.completedByEmail || undefined
      );
  }
}

/**
 * Data-URL in Blob konvertieren
 */
export const dataUrlToBlob = (dataUrl: string): Blob => {
  const [meta, base64] = dataUrl.split(",");
  const mime = meta.match(/:(.*?);/)?.[1];
  return new Blob([base64ToBytes(base64)], { type: mime });
};

/**
 * Sync-State aus localStorage löschen (bei Logout)
 */
export const clearSyncState = (): void => {
  try {
    localStorage.removeItem("sync-state");
    localStorage.removeItem("sync-timestamp");
    localStorage.removeItem("sync-last-result");
    console.log("Sync-State cleared");
  } catch (error) {
    console.error("Fehler beim Löschen des Sync-State:", error);
  }
};
